import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { logout } from "../firebase/auth"; // Import logout function
import useAccessLevel from "../hooks/useAccessLevel";
import { gradeToken } from "../config/plans";
import { portalViewFor, effectiveRoleKind, ROLE_KIND } from "../utils/accountRole";
import CoinCounter from "../components/coins/CoinCounter";
import ShopButton from "../components/shop/ShopButton";
import DashboardHero from "../components/dashboard/DashboardHero";
import { formalName, friendlyName } from "../utils/displayName";
import AvatarMaker from "../components/avatar/AvatarMaker";
import LeaderboardButton from "../components/leaderboard/LeaderboardButton";
import BuyCoinsButton from "../components/coins/BuyCoinsButton";
import RewardsBar from "../components/rewards/RewardsBar";
import FreeTierBanner from "../components/FreeTierBanner";
import ProductSpotlight from "../components/ProductSpotlight";
import SubscribeNow from "../components/SubscribeNow";
import RefreshAccessButton from "../components/RefreshAccessButton";
import { NAV_SUBJECTS } from "../data/subjects";
import useClassEducator from "../hooks/useClassEducator";
import "../styles/sbl-ui.css";
import "../components/dashboard/dashboard.css";
import { logPageView } from "../utils/logPageView";
import { checkAndExpireTrial } from "../utils/checkTrialExpiration";


const HintsOverlay = ({
  targets,           // { key: { ref, text, placement } }
  onDismiss,         // () => void
  canDismiss,        // boolean
}) => {
  const [positions, setPositions] = useState({});
 
  const compute = () => {
    const next = {};
    Object.entries(targets).forEach(([key, { ref, placement = 'top' }]) => {
      const el = ref?.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // default bubble position near target with a little offset
      const margin = 10;
      let top = rect.top - margin;
      let left = rect.left + rect.width / 2;

      if (placement === 'top') {
        top = rect.top - margin;
        left = rect.left + rect.width / 2;
      } else if (placement === 'right') {
        top = rect.top + rect.height / 2;
        left = rect.right + margin;
      } else if (placement === 'left') {
        top = rect.top + rect.height / 2;
        left = rect.left - margin;
      } else if (placement === 'bottom') {
        top = rect.bottom + margin;
        left = rect.left + rect.width / 2;
      }

      next[key] = { top, left, rect, placement };
    });
    setPositions(next);
  };

  useLayoutEffect(() => {
    compute();
    const onResize = () => compute();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [targets]);

  return (
    <>
      {/* screen-wide overlay container */}
      <div style={overlayStyles.layer} aria-hidden>
        {Object.entries(targets).map(([key, { text }]) => {
          const pos = positions[key];
          if (!pos) return null;

          // compute bubble top/left from centerpoint
          const bubbleOffset = 14; // px away from the target edge
          const bubbleStyle = { ...overlayStyles.bubble };
          const arrowStyle = { ...overlayStyles.arrow };

          let bubbleTop = pos.top;
          let bubbleLeft = pos.left;

          if (pos.placement === 'top') {
            bubbleTop = pos.rect.top - 8 - bubbleOffset;
            bubbleLeft = pos.rect.left + pos.rect.width / 2;
            arrowStyle.borderTopColor = '#111';
            arrowStyle.top = bubbleTop + overlayStyles.bubble.padding + 28;
            arrowStyle.left = bubbleLeft;
            arrowStyle.transform = 'translate(-50%, 0) rotate(180deg)';
          } else if (pos.placement === 'bottom') {
            bubbleTop = pos.rect.bottom + bubbleOffset;
            bubbleLeft = pos.rect.left + pos.rect.width / 2;
            arrowStyle.borderTopColor = '#111';
            arrowStyle.top = bubbleTop - 8;
            arrowStyle.left = bubbleLeft;
            arrowStyle.transform = 'translate(-50%, 0)';
          } else if (pos.placement === 'right') {
            bubbleTop = pos.rect.top + pos.rect.height / 2;
            bubbleLeft = pos.rect.right + bubbleOffset;
            arrowStyle.borderRightColor = '#111';
            arrowStyle.top = bubbleTop;
            arrowStyle.left = bubbleLeft - 8;
            arrowStyle.transform = 'translate(0, -50%) rotate(-90deg)';
          } else if (pos.placement === 'left') {
            bubbleTop = pos.rect.top + pos.rect.height / 2;
            bubbleLeft = pos.rect.left - bubbleOffset;
            arrowStyle.borderLeftColor = '#111';
            arrowStyle.top = bubbleTop;
            arrowStyle.left = bubbleLeft + 8;
            arrowStyle.transform = 'translate(0, -50%) rotate(90deg)';
          }

          bubbleStyle.top = bubbleTop;
          bubbleStyle.left = bubbleLeft;
          bubbleStyle.transform = 'translate(-50%, -100%)'; // nice default; overridden by placements below

          if (pos.placement === 'bottom') bubbleStyle.transform = 'translate(-50%, 0)';
          if (pos.placement === 'right')  bubbleStyle.transform = 'translate(0, -50%)';
          if (pos.placement === 'left')   bubbleStyle.transform = 'translate(-100%, -50%)';

          return (
            <React.Fragment key={key}>
              <div style={arrowStyle} />
              <div style={bubbleStyle}>
                {text}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* control pill */}
      {canDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          style={overlayStyles.dismissButton}
        >
          Hide Tips
        </button>
      )}
    </>
  );
};

const overlayStyles = {
  layer: {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none', // do not block UI
    zIndex: 9999,
  },
  bubble: {
    position: 'fixed',
    maxWidth: 320,
    background: '#111',
    color: '#fff',
    fontSize: 14,
    lineHeight: 1.35,
    padding: 12,
    borderRadius: 8,
    boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
    border: '2px solid #fff',
    pointerEvents: 'auto', // allow text selection if needed
  },
  arrow: {
    position: 'fixed',
    width: 0,
    height: 0,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderTop: '8px solid #111', // default pointing down; we rotate per placement
    pointerEvents: 'none',
    zIndex: 9999,
  },
  dismissButton: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    zIndex: 10000,
    background: '#222',
    color: '#fff',
    border: '2px solid #fff',
    borderRadius: 999,
    padding: '10px 16px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 6px 14px rgba(0,0,0,0.35)',
  },
  showButton: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    zIndex: 10000,
    background: '#0d9488', // teal so it looks different
    color: '#fff',
    border: '2px solid #fff',
    borderRadius: 999,
    padding: '10px 16px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 6px 14px rgba(0,0,0,0.35)',
  },
};

const Dashboard = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAvatarPopup, setShowAvatarPopup] = useState(false);
  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();
  const [educatorStatus, setEducatorStatus] = useState("active"); 
// "active" | "trial" | "expired"

// Same fix as DashboardEducator: this used to read trial.status alone, and
// isUserBlocked() below (the only payment-aware code here) was never called,
// so a paid subscription never unlocked the page.
const { isPaid, isFree, hasPaidSubscription, loading: accessLoading } =
    useAccessLevel();
const isAccessExpired =
  !accessLoading && !isPaid && userData?.trial?.status === "expired";


  // Tips always start hidden — on every visit and every refresh. They show
  // only after "Show Tips" is clicked, and only until the page is left.
  const [hideTips, setHideTips] = useState(true);
  const { educator: classTeacher } = useClassEducator(); // the student's teacher (name for the code line)

  // refs for coach marks
  const subjectRowRef     = useRef(null);
  const avatarEditRef     = useRef(null);
  // What the student is wearing from the shop (live).
  const scienceBtnRef     = useRef(null); // 👈 Added this line

// --- Tip handlers ---
const dismissTips = () => setHideTips(true);

const showTips = () => setHideTips(false);
// LIVE, not a one-shot read.
//
// This was a single getDoc on mount. The Stripe webhook writes the promoted
// role and the subscription seconds AFTER the redirect back, so a dashboard
// that read once and never looked again showed "unpaid" until the person
// happened to hard-refresh — which is exactly what it looked like when
// someone paid and the badge didn't change.
//
// It also waited on `auth.currentUser`, which is null on a cold load while
// Firebase restores the session, so a direct visit to /dashboard could render
// "No user data found." onAuthStateChanged removes that race too.
useEffect(() => {
  let unsubDoc = null;
  let expiryChecked = false;

  const unsubAuth = onAuthStateChanged(auth, (user) => {
    if (unsubDoc) {
      unsubDoc();
      unsubDoc = null;
    }

    if (!user) {
      setUserData(null);
      setLoading(false);
      return;
    }

    const userRef = doc(db, "users", user.uid);

    unsubDoc = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) {
          setUserData(null);
          setLoading(false);
          return;
        }

        const data = snap.data();
        setUserData(data);
        setEducatorStatus(data?.trial?.status || "active");
        setLoading(false);

        // Runs once per mount. checkAndExpireTrial writes back to the same
        // document, so calling it on every snapshot would have the write
        // re-trigger the listener that triggered the write.
        if (!expiryChecked) {
          expiryChecked = true;
          checkAndExpireTrial(userRef, data).catch((err) =>
            console.warn("Trial expiry check failed:", err?.message || err)
          );
        }
      },
      (error) => {
        console.error("Error reading user data:", error);
        setLoading(false);
      }
    );
  });

  return () => {
    if (unsubDoc) unsubDoc();
    unsubAuth();
  };
}, [auth, db]);

 function isTrialExpiredByDate(user) {
  const end = user?.trial?.endDate;
  if (!end) return false;

  const endDate = end?.toDate ? end.toDate() : new Date(end);
  return endDate.getTime() < Date.now();
}

  
  useEffect(() => {
    logPageView("/dashboard");
  }, []);

  // An educator who lands here belongs on the educator dashboard.
  //
  // Several paths lead here that shouldn't: the "/" default route sends
  // anyone without "educator" in their role string, and role is "unpaid"
  // until the webhook promotes it. Rather than fix each caller and hope none
  // is added later, this page turns them around itself. Safe from looping —
  // DashboardEducator has no matching redirect back.
  useEffect(() => {
    if (!userData) return;
    if (effectiveRoleKind(userData) === ROLE_KIND.EDUCATOR) {
      navigate("/educator/dashboard", { replace: true });
    }
  }, [userData, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error.message);
    }
  };

  const handleAvatarEdit = () => {
    setShowAvatarPopup(true);
  };

  const handleClosePopup = () => {
    setShowAvatarPopup(false);
  };

  // The old picture picker's save handler lived here; the avatar maker
  // saves through the saveAvatarLook Cloud Function.

  
  // isUserBlocked() lived here and was never called from anywhere in this
  // file. Its logic now belongs to useAccessLevel, which reads the
  // subscription document, seat assignments, and the admin claim together.
  
  const handleSubjectClick = (subject) => {
    // This used to be `userData.role.split("-")`, which only works once a
    // role has been granted. A free account's role is "unpaid", so that
    // produced /portal/unpaid/undefined/{subject}/student and a dead page.
    //
    // Registration stores state and grade as their own fields, so prefer
    // those and fall back to parsing the role for accounts created before
    // the signup flow existed.
    const [roleState, roleGrade] = String(userData.role || "").split("-");
    const state = (userData.state || roleState || "").toLowerCase();
    const grade = userData.grade
      ? gradeToken(userData.grade)
      : roleGrade;

    if (!state || !grade) {
      console.warn("No state/grade on this account; sending to plan page.");
      navigate("/choose-plan");
      return;
    }

    // The view was hardcoded to "student". An educator whose role is still
    // "unpaid" lands on this dashboard (see the Login redirect), and this
    // line then sent them into the student portal — the "I signed up as an
    // educator but I'm seeing the student pages" report. portalViewFor()
    // reads the granted role first and falls back to intendedRole, so it is
    // right before the webhook has promoted anyone.
    const view = portalViewFor(userData);

    navigate(`/portal/${state}/${grade}/${subject}/${view}`);
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  if (!userData) {
    return <p>No user data found.</p>;
  }

  // Shared with the portal — see src/data/subjects.js.
  const subjects = NAV_SUBJECTS;
  const colors = ["#007bff", "#28a745", "#dc3545", "#ffc107"]; // Blue, Green, Red, Yellow
  const backgroundImageUrl = userData?.backgroundImageUrl || ""; // or hardcode a URL to test

  const isParent = effectiveRoleKind(userData) === ROLE_KIND.PARENT;

  return (
    <div
  style={{
    ...styles.dashboardContainer,
    ...(backgroundImageUrl
      ? {
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }
      : {}),
  }}
>
{isFree && <FreeTierBanner label="subjects" />}

{isAccessExpired && (
  <div
    style={{
      background: "#ffe4e6",
      border: "2px solid #dc2626",
      padding: "12px 16px",
      marginBottom: 16,
      borderRadius: 8,
      fontWeight: 700,
      color: "#7f1d1d",
    }}
  >
    Your classroom access has ended.  
    Please ask your teacher to renew their subscription.
  </div>
)}

      {/* Top card — avatar, a greeting that says what to do next, and one
          centred row of buttons (components/dashboard/DashboardHero.js). */}
      <DashboardHero
        variant="student"
        profile={userData}
        name={friendlyName(userData, "friend")}
        onEditAvatar={handleAvatarEdit}
        editRef={avatarEditRef}
        line={
          isParent ? (
            <>Follow what your child is learning, and <b>add coins</b> to their account as a reward.</>
          ) : (
            <>Ready to learn? Pick a subject below — every game, song and video you finish earns <b>coins</b> for your avatar.</>
          )
        }
        chips={[
          userData.educatorCode && (
            <>
              🏫 {classTeacher?.lastName || classTeacher?.displayName ? `${formalName(classTeacher)}'s class` : "My class"} <code>{userData.educatorCode}</code>
            </>
          ),
        ]}
        actions={
          <>
            <CoinCounter />
            <ShopButton />
            <LeaderboardButton code={userData.educatorCode || ""} />
            {/* parents can add coins to their child's account; students never see this */}
            {isParent && <BuyCoinsButton />}
            {/* step 5: daily treasure trail, gifts, sticker albums */}
            <RewardsBar />
          </>
        }
      />

      {/* Choose a subject */}
      <section className="sbl-subjects">
        <header className="sbl-subjects-head">
          <p className="sbl-subjects-kicker">Let's learn</p>
          <h2 className="sbl-subjects-title">Choose a subject</h2>
          <p className="sbl-subjects-sub">Each subject has topics with songs, videos and games made for your grade.</p>
        </header>

        <div className="sbl-subjects-row" ref={subjectRowRef}>
          {subjects.map((subject) => (
            <button
              key={subject.value}
              type="button"
              ref={subject.value === "science" ? scienceBtnRef : null}
              className={`sbl-tile ${subject.className}`}
              disabled={isAccessExpired}
              aria-disabled={isAccessExpired}
              title={isAccessExpired ? "Your access has ended — upgrade to continue" : undefined}
              onClick={() => {
                if (isAccessExpired) return;
                handleSubjectClick(subject.value);
              }}
            >
              <span className="sbl-tile-icon" aria-hidden="true">
                {subject.icon}
              </span>
              <span className="sbl-tile-label">{subject.label}</span>
              {subject.hint && <span className="sbl-tile-hint">{subject.hint}</span>}
            </button>
          ))}

          {/* Educator-Specific Button (an educator who landed here). */}
          {effectiveRoleKind(userData) === ROLE_KIND.EDUCATOR && (
            <button type="button" className="sbl-tile sbl-tile-educator" onClick={() => navigate("/educator/dashboard")}>
              <span className="sbl-tile-icon" aria-hidden="true">🧑‍🏫</span>
              <span className="sbl-tile-label">Educator Tools</span>
              <span className="sbl-tile-hint">Your teacher dashboard</span>
            </button>
          )}

          {/* Administrator Button */}
          {effectiveRoleKind(userData) === ROLE_KIND.ADMIN && (
            <button type="button" className="sbl-tile sbl-tile-admin" onClick={() => navigate("/admin")}>
              <span className="sbl-tile-icon" aria-hidden="true">🛠️</span>
              <span className="sbl-tile-label">Admin Dashboard</span>
              <span className="sbl-tile-hint">Settings & tools</span>
            </button>
          )}
        </div>
      </section>

      {/* Pay now instead of waiting for the trial to end. Gated on
          hasPaidSubscription, not isPaid — a trial counts as PAID for access,
          so isPaid would have hidden this from every trial user. */}
      {!hasPaidSubscription && (
        <>
          <SubscribeNow profile={userData} uid={getAuth().currentUser?.uid} />
          {/* The escape hatch for a payment the webhook never landed. */}
          <RefreshAccessButton />
        </>
      )}

      {/* Bottom: Logout + Account settings, then (grown-ups only) the shop
          banner underneath — one centred column. */}
      <div className="sbl-dash-bottom">
        <div className="sbl-dash-bottom-row">
          <button className="sbl-btn sbl-btn-danger sbl-btn-lg" onClick={handleLogout}>
            Logout
          </button>
          {/* Password, email and subscription (incl. cancelling) */}
          <button type="button" className="sbl-account-link" onClick={() => navigate("/account")}>
            ⚙️ Account settings
          </button>
          {/* the child's learning report — for parents (and anyone curious) */}
          <button type="button" className="sbl-account-link" onClick={() => navigate("/grown-ups")}>
            👪 {isParent ? "Your child's report" : "For grown-ups"}
          </button>
        </div>
        {/* Our products are for grown-ups: students never see the banner. */}
        {effectiveRoleKind(userData) !== ROLE_KIND.STUDENT && <ProductSpotlight heading="From the shop" />}
      </div>

      {showAvatarPopup && (
        <AvatarMaker onClose={handleClosePopup} />
      )}
      {/* Show Tips button (only when hidden) */}
{hideTips && (
  <button
    type="button"
    onClick={showTips}
    className="sbl-show-tips"
    style={overlayStyles.showButton}
  >
    Show Tips
  </button>
)}

{/* Instruction overlays (if not hidden) */}
{!hideTips && (
  <HintsOverlay
    canDismiss
    onDismiss={dismissTips}
    targets={{
      science: {
        ref: scienceBtnRef,
        placement: "left",
        text: "Choose Science to explore your science learning topics.",
      },
      avatarEdit: {
        ref: avatarEditRef,
        placement: "right",
        text: "Click Edit to customize your avatar.",
      },
      subjectRow: {
        ref: subjectRowRef,
        placement: "top",
        text: "Tap any subject to explore fun lessons and quizzes!",
      },
    }}
  />
)}

    </div>
  );
};

const styles = {
  dashboardContainer: {
    textAlign: "center",
    padding: "20px",
    backgroundColor: "#e0f7ff",
    minHeight: "100vh",
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
  },
  logo: {
    height: "80px",
  },
  centerContainer: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    padding: "20px",
    margin: "20px auto",
    maxWidth: "600px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  },
  avatarContainer: {
    position: "relative",
    marginBottom: "20px",
  },
  avatar: {
    width: "100px",
    height: "100px",
    borderRadius: "50%",
    objectFit: "cover",
  },
  avatarButton: {
    position: "absolute",
    bottom: "0",
    right: "0",
    width: "60px",
    height: "60px",
    backgroundColor: "#16a085", // Teal
    color: "#fff",
    fontSize: "18px",
    border: "2px solid #222",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 4px 0 rgba(0, 0, 0, 0.3), 0 6px 8px rgba(0, 0, 0, 0.3)",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  subscriptionBadge: {
    backgroundColor: "#8e44ad", // Purple
    color: "#fff",
    fontWeight: "bold",
    fontSize: "16px",
    padding: "6px 12px",
    borderRadius: "6px",
    display: "inline-block",
    boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
    border: "2px solid #222",
    margin: "10px 0",
  },
  welcomeText: {
    marginTop: "30px",
    fontSize: "16px",
  },
  subjectButtonContainer: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "20px",
    marginTop: "20px",
  },
  
  subjectButton: {
    width: "200px",
    height: "200px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    borderRadius: "5px",
    color: "#fff",
    fontSize: "25px",
    fontWeight: "bold",
    textAlign: "center",
    padding: "10px 20px",
    border: "2px solid #222", // Adds depth
    boxShadow: "0 6px 0 rgba(0, 0, 0, 0.4), 0 8px 10px rgba(0, 0, 0, 0.4)", // Layered shadow
    transition: "transform 0.2s, box-shadow 0.2s", // Smooth interaction
    backgroundColor: "#4a90e2", // Default to blue, can override with color variants below
  },
  
  buttonText: {
    color: "#fff",
  },
  logoutButton: {
    width: "200px",
    height: "75px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px 20px",
    fontSize: "22px",
    fontWeight: "bold",
    color: "#fff",
    backgroundColor: "#c0392b",
    border: "2px solid #222",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 rgba(0, 0, 0, 0.4), 0 8px 10px rgba(0, 0, 0, 0.4)",
    transition: "transform 0.2s, box-shadow 0.2s",
    marginTop: "40px", // 👈 space from subject buttons
  },
  scienceButton: {
    backgroundColor: "#4a90e2", // Blue
  },
  
  socialStudiesButton: {
    backgroundColor: "#4CAF50", // Green
  },
  
  mathButton: {
    backgroundColor: "#FF6347", // Reddish
  },
  
  otherButton: {
    backgroundColor: "#FFD700", // Yellow
  },
  
  homeButton: {
    backgroundColor: "#A020F0", // Purple
  },
  
  
};

export default Dashboard;
